<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink, type RouteLocationRaw } from 'vue-router'

import { useI18n } from '@/shared/i18n'
import { Button } from '@/shared/ui/button'

export type OutcomeKind = 'applied' | 'waiting' | 'skipped' | 'failed'

export interface OutcomeRow {
  rowKey: string
  label: string
  retryable?: boolean
}

export interface OutcomeBucket {
  kind: OutcomeKind
  count: number
  rows: OutcomeRow[]
  message?: string
  title?: string
  to?: RouteLocationRaw
  toLabel?: string
}

const props = defineProps<{ submitted: number; buckets: OutcomeBucket[] }>()
const emit = defineEmits<{ retry: [rowKeys: string[]] }>()

const { t } = useI18n()

const ORDER: OutcomeKind[] = ['applied', 'waiting', 'skipped', 'failed']

const TERM_KEY: Record<OutcomeKind, string> = {
  applied: 'insightActions.outcome.termApplied',
  waiting: 'insightActions.outcome.termWaiting',
  skipped: 'insightActions.outcome.termSkipped',
  failed: 'insightActions.outcome.termFailed',
}
const TITLE_KEY: Record<OutcomeKind, string> = {
  applied: 'insightActions.outcome.applied',
  waiting: 'insightActions.outcome.waiting',
  skipped: 'insightActions.outcome.skipped',
  failed: 'insightActions.outcome.failed',
}
const TONE: Record<OutcomeKind, string> = {
  applied: 'border-l-success',
  waiting: 'border-l-info',
  skipped: 'border-l-subtle',
  failed: 'border-l-danger',
}

const byKind = computed(() => new Map(props.buckets.map((bucket) => [bucket.kind, bucket])))

function count(kind: OutcomeKind): number {
  return byKind.value.get(kind)?.count ?? 0
}

const equation = computed(() => {
  const terms = ORDER.map((kind) =>
    t('insightActions.outcome.term', {
      count: count(kind),
      label: byKind.value.get(kind)?.title ?? t(TERM_KEY[kind]),
    }),
  ).join(' + ')
  return t('insightActions.outcome.equation', { submitted: props.submitted, terms })
})

const hasFailures = computed(() => count('failed') > 0)

const shown = computed(() =>
  ORDER.map((kind) => byKind.value.get(kind)).filter((bucket): bucket is OutcomeBucket =>
    Boolean(bucket?.count),
  ),
)

const retryableKeys = computed(() =>
  (byKind.value.get('failed')?.rows ?? []).filter((row) => row.retryable).map((row) => row.rowKey),
)

function preview(rows: OutcomeRow[]): string {
  const names = rows.map((row) => row.label).filter(Boolean)
  const head = names.slice(0, 3).join(' · ')
  return names.length > 3
    ? t('insightActions.outcome.andMore', { names: head, count: names.length - 3 })
    : head
}
</script>

<template>
  <div class="action-outcome grid gap-sm min-w-0">
    <p
      class="m-0 rounded-md px-sm py-xs text-xs"
      :class="hasFailures ? 'bg-warning-soft text-warning' : 'bg-success-soft text-success'"
      data-testid="outcome-equation"
    >
      {{ equation }}
    </p>

    <div
      v-for="bucket in shown"
      :key="bucket.kind"
      class="rounded-r-md border border-l-[3px] border-subtle px-md py-sm"
      :class="TONE[bucket.kind]"
      :data-bucket="bucket.kind"
    >
      <h4 class="m-0 text-sm font-semibold">
        {{ bucket.count }} {{ bucket.title ?? t(TITLE_KEY[bucket.kind]) }}
      </h4>
      <p v-if="bucket.message" class="mt-2xs mb-0 text-xs text-muted">{{ bucket.message }}</p>
      <p v-if="bucket.rows.length" class="mt-2xs mb-0 truncate text-xs text-muted">
        {{ preview(bucket.rows) }}
      </p>

      <RouterLink
        v-if="bucket.to"
        :to="bucket.to"
        class="mt-xs inline-block text-xs font-semibold"
        :data-testid="`outcome-go-${bucket.kind}`"
      >
        {{ bucket.toLabel }}
      </RouterLink>

      <Button
        v-if="bucket.kind === 'failed' && retryableKeys.length"
        variant="ghost"
        size="xs"
        data-testid="outcome-retry"
        @click="emit('retry', retryableKeys)"
      >
        {{ t('insightActions.outcome.retry', { count: retryableKeys.length }) }}
      </Button>
    </div>

    <p class="m-0 text-2xs text-muted">{{ t('insightActions.outcome.accounted') }}</p>
  </div>
</template>
